import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext.jsx';
import { fetchWhatsNew, updateWhatsNew } from '../lib/whatsNew.js';
import WhatsNewForm from '../components/whatsNew/WhatsNewForm.jsx';

export default function WhatsNewEdit() {
  const { id } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isAdmin, isActive } = useAuth();
  const [item, setItem] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // `t` deliberately excluded — see ScholarsEdit for the rationale.
  useEffect(() => {
    let cancelled = false;
    fetchWhatsNew(id)
      .then((row) => !cancelled && (row ? setItem(row) : setError(t('whatsNew.form.notFound'))))
      .catch((err) => !cancelled && setError(err.message ?? String(err)));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (!isActive) {
    return (
      <div className="mx-auto max-w-2xl px-8 pb-20 pt-32">
        <div className="cta-box">
          <h1 className="cta-title">{t('bibliography.form.notActiveTitle')}</h1>
          <p className="cta-body">{t('bibliography.form.notActiveBody')}</p>
        </div>
      </div>
    );
  }
  if (error) {
    return (
      <div className="mx-auto max-w-2xl px-8 pb-20 pt-32 text-center">
        <h1 className="font-serif text-3xl text-gold-light">{t('common.error')}</h1>
        <p className="mt-3 text-ink-secondary">{error}</p>
      </div>
    );
  }
  if (!item) {
    return (
      <div role="status" aria-live="polite" className="mx-auto max-w-3xl px-8 pb-20 pt-32 text-center text-ink-secondary">
        {t('common.loading')}
      </div>
    );
  }
  if (!isAdmin) {
    return (
      <div className="mx-auto max-w-2xl px-8 pb-20 pt-32 text-center">
        <h1 className="font-serif text-3xl text-gold-light">{t('common.error')}</h1>
        <p className="mt-3 text-ink-secondary">{t('whatsNew.form.adminOnly')}</p>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="mx-auto max-w-2xl px-8 pb-20 pt-32">
        <div className="cta-box">
          <div className="section-label">{t('whatsNew.form.savedLabel')}</div>
          <h1 className="cta-title">{item.title}</h1>
          <p className="cta-body">{t('whatsNew.form.savedBody')}</p>
          <button type="button" className="btn-primary" onClick={() => navigate('/whats-new')}>
            {t('whatsNew.form.backToList')}
          </button>
        </div>
      </div>
    );
  }

  async function handleSubmit(form) {
    setBusy(true);
    try {
      await updateWhatsNew(item.id, form);
      setSubmitted(true);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mx-auto max-w-3xl px-6 pb-20 pt-28">
      <header className="mb-8">
        <div className="section-label">{t('whatsNew.form.editLabel')}</div>
        <h1 className="font-serif text-3xl font-semibold text-cream">{item.title}</h1>
      </header>

      <WhatsNewForm
        initialValue={item}
        submitLabel={t('whatsNew.form.save')}
        onSubmit={handleSubmit}
        onCancel={() => navigate('/whats-new')}
        busy={busy}
      />
    </div>
  );
}
